import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { License, LicenseState, LimitLevel } from '../../api/licenses';
import { formatDate } from '../../util/datetime';

const STATE: Record<LicenseState, { label: string; color: 'success' | 'warning' | 'error' }> = {
  active: { label: 'Active', color: 'success' },
  grace: { label: 'Ended — grace period', color: 'warning' },
  read_only: { label: 'Read-only', color: 'error' },
};

/** Active / grace / read-only, plus "expires soon" inside 45 days. */
export function LicenseStateChip({ license }: { license: License }) {
  if (license.revoked_at) return <Chip size="small" color="error" label="Revoked — read-only" />;
  const meta = STATE[license.state];
  const soon =
    license.state === 'active' && license.days_left !== null && license.days_left <= 45;
  return (
    <Chip
      size="small"
      color={soon ? 'warning' : meta.color}
      label={soon ? `Ends in ${license.days_left} day${license.days_left === 1 ? '' : 's'}` : meta.label}
    />
  );
}

const LEVEL_COLOR: Record<LimitLevel, 'primary' | 'warning' | 'error'> = {
  ok: 'primary',
  warn: 'warning',
  over: 'warning',
  blocked: 'error',
};

/** "12 of 50 participants" with a bar; "no limit" when there is none. */
export function UsageBar({
  label,
  used,
  limit,
  level,
}: {
  label: string;
  used: number;
  limit: number | null;
  level: LimitLevel;
}) {
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="body2">
        {limit ? `${used} of ${limit} ${label}` : `${used} ${label} · no limit`}
        {level === 'over' && ' — over the limit'}
        {level === 'blocked' && (label === 'procedures' ? ' — limit reached' : ' — new ones refused')}
      </Typography>
      {limit ? (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (used / limit) * 100)}
          color={LEVEL_COLOR[level]}
          aria-label={`${label} used`}
          sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
        />
      ) : null}
    </Box>
  );
}

/** Key, dates, state and pooled usage — the same block for tenant admins
 *  (Settings → License) and platform admins (tenant page, Licenses). */
export function LicenseSummary({ license }: { license: License }) {
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography
          component="code"
          sx={{ fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: 600 }}
        >
          {license.key}
        </Typography>
        <LicenseStateChip license={license} />
        {license.kind === 'trial' && <Chip size="small" variant="outlined" label="Trial" />}
      </Stack>
      {license.main_key && (
        <Typography variant="body2" color="text.secondary">
          Licensed through <strong>{license.main_tenant_name}</strong> (license{' '}
          <code>{license.main_key}</code>). Its dates and limits apply here, shared with the
          other organisations under it.
        </Typography>
      )}
      <Typography variant="body2">
        {license.starts_at && <>From {formatDate(license.starts_at)} </>}
        {license.ends_at && <>until {formatDate(license.ends_at)}</>}
        {license.state === 'grace' && license.grace_ends_at && (
          <> · full access until {formatDate(license.grace_ends_at)}, then read-only</>
        )}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        <UsageBar
          label="participants"
          used={license.seats_used}
          limit={license.max_seats}
          level={license.seat_level}
        />
        <UsageBar
          label="devices"
          used={license.devices_used}
          limit={license.max_devices}
          level={license.device_level}
        />
        <UsageBar
          label="procedures"
          used={license.procedures_used}
          limit={license.max_procedures}
          level={license.procedure_level}
        />
      </Stack>
    </Stack>
  );
}

/** Every tenant's license — a sub-license's tenant maps to its main
 *  license (whose state and dates it follows), with its own key. */
export function licensesByTenant(list: License[]): Map<string, License> {
  const out = new Map<string, License>();
  for (const l of list) {
    out.set(l.tenant_id, l);
    for (const s of l.subs) {
      out.set(s.tenant_id, {
        ...l, id: s.id, key: s.key, tenant_id: s.tenant_id, tenant_name: s.tenant_name,
        main_key: l.key, main_tenant_name: l.tenant_name, subs: [],
      });
    }
  }
  return out;
}

/** One short status for a tenant list: Unlicensed · Trial: N days left ·
 *  Licensed · Ends in N days · Ended — grace · Read-only. */
export function licenseStatus(l: License | undefined): {
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
} {
  if (!l) return { label: 'Unlicensed', color: 'default' };
  if (l.revoked_at) return { label: 'Revoked — read-only', color: 'error' };
  if (l.state === 'read_only') return { label: 'Read-only', color: 'error' };
  if (l.state === 'grace') return { label: 'Ended — grace', color: 'warning' };
  const days = l.days_left ?? 0;
  const left = `${days} day${days === 1 ? '' : 's'}`;
  if (l.kind === 'trial') return { label: `Trial: ${left} left`, color: days <= 7 ? 'warning' : 'info' };
  if (days <= 45) return { label: `Ends in ${left}`, color: 'warning' };
  return { label: 'Licensed', color: 'success' };
}
